
import React, { useState } from 'react';
import { Language, UserRole } from '../types';
import { TRANSLATIONS } from '../translations';
import { Share2, Copy, Check, Users, DollarSign, Gift, Twitter, Facebook, Send } from 'lucide-react';

interface ReferralViewProps {
  language: Language;
  userRole?: UserRole;
}

export const ReferralView: React.FC<ReferralViewProps> = ({ language, userRole }) => {
  const t = TRANSLATIONS[language];
  const [copied, setCopied] = useState(false);
  
  // Custom link based on role for demo
  const isProject = userRole === UserRole.PROJECT;
  const referralLink = isProject ? "https://linkkol.com/join?ref=supernova" : "https://linkkol.com/join?ref=cryptowiz";

  const handleCopy = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Mock Data
  const stats = {
      totalInvites: 12,
      commission: 450,
      pending: 50
  };

  const invites = [
      { id: 1, user: "@NFT_Degen", date: "2023-10-20", status: "Active", earned: 120 },
      { id: 2, user: "@SolanaGirl", date: "2023-10-22", status: "Pending", earned: 0 },
      { id: 3, user: "@AlphaHunter", date: "2023-10-25", status: "Active", earned: 85 },
  ];

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="bg-gradient-to-r from-indigo-900 to-purple-900 border border-indigo-500/30 rounded-xl p-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none"></div>
          
          <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-8">
              <div>
                  <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
                      <Gift className="text-yellow-400" size={32} />
                      {t.inviteEarn}
                  </h1>
                  <p className="text-indigo-200 text-lg max-w-xl">
                      {isProject ? t.referralSubtitleProject : t.referralSubtitle}
                  </p>
              </div>
              
              <div className="bg-slate-900/50 p-6 rounded-xl border border-indigo-500/30 backdrop-blur-sm min-w-[300px]">
                  <p className="text-sm text-slate-400 mb-2">{t.yourLink}</p>
                  <div className="flex gap-2">
                      <div className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white font-mono text-sm truncate">
                          {referralLink}
                      </div>
                      <button 
                        onClick={handleCopy}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white p-2 rounded-lg transition-colors flex items-center justify-center"
                      >
                          {copied ? <Check size={18} /> : <Copy size={18} />}
                      </button>
                  </div>
                  <div className="flex gap-4 mt-4 justify-center">
                      <button className="p-2 bg-[#1DA1F2]/20 text-[#1DA1F2] rounded-lg hover:bg-[#1DA1F2]/30 transition-colors">
                          <Twitter size={20} />
                      </button>
                      <button className="p-2 bg-[#1877F2]/20 text-[#1877F2] rounded-lg hover:bg-[#1877F2]/30 transition-colors">
                          <Facebook size={20} />
                      </button>
                      <button className="p-2 bg-[#229ED9]/20 text-[#229ED9] rounded-lg hover:bg-[#229ED9]/30 transition-colors">
                          <Send size={20} />
                      </button>
                  </div>
              </div>
          </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-blue-500/20 rounded-lg text-blue-400">
                      <Users size={20} />
                  </div>
                  <span className="text-slate-400 font-medium">{t.totalInvites}</span>
              </div>
              <h3 className="text-3xl font-bold text-white">{stats.totalInvites}</h3>
          </div>
          
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-emerald-500/20 rounded-lg text-emerald-400">
                      <DollarSign size={20} />
                  </div>
                  <span className="text-slate-400 font-medium">{t.commissionEarned}</span>
              </div>
              <h3 className="text-3xl font-bold text-white">${stats.commission}</h3>
          </div>

          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-amber-500/20 rounded-lg text-amber-400">
                      <Share2 size={20} />
                  </div>
                  <span className="text-slate-400 font-medium">Pending Payout</span>
              </div>
              <h3 className="text-3xl font-bold text-white">${stats.pending}</h3>
          </div>
      </div>

      <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
          <div className="p-6 border-b border-slate-700">
              <h3 className="font-bold text-white text-lg">{t.recentInvites}</h3>
          </div>
          <table className="w-full text-left">
              <thead className="bg-slate-900/50 text-slate-400 text-xs uppercase tracking-wider">
                  <tr>
                      <th className="px-6 py-4">User</th>
                      <th className="px-6 py-4">Date Joined</th>
                      <th className="px-6 py-4">Status</th>
                      <th className="px-6 py-4">Earned</th>
                  </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                  {invites.map(invite => (
                      <tr key={invite.id} className="hover:bg-slate-700/30 transition-colors">
                          <td className="px-6 py-4 font-bold text-white">{invite.user}</td>
                          <td className="px-6 py-4 text-slate-400 text-sm">{invite.date}</td>
                          <td className="px-6 py-4">
                              <span className={`px-2 py-1 rounded text-xs font-bold ${
                                  invite.status === 'Active' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'
                              }`}>
                                  {invite.status}
                              </span>
                          </td>
                          <td className="px-6 py-4 text-white font-mono">+${invite.earned}</td>
                      </tr>
                  ))}
              </tbody>
          </table>
      </div>
    </div>
  );
};
